import React, { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '../contexts/AuthContext';
import { api } from '../utils/api';

interface PartnerMachine {
  id: string;
  code: string;
  location: string;
  city?: string;
}

// The partner-scoped report: only the commercial point's side of the repasse. The
// owner's share, the gross/operational breakdown and the customer names are not sent.
interface RepasseReport {
  machine: { number: string; address: string; city: string };
  referencePeriod: { startDate: string; endDate: string };
  quotas: { commercialPoint: number };
  repasse: {
    commercialPointValue: number;
    commercialPointCustody: number;
    commercialPointReceivePay: number;
  };
  usageHistory: Array<{
    date: string;
    duration: number;
    amount: number;
    paymentMethod: string;
  }>;
  totals: { custodyValue: number; finalValue: number };
}

const today = () => new Date().toISOString().slice(0, 10);
const thirtyDaysAgo = () => {
  const d = new Date();
  d.setDate(d.getDate() - 30);
  return d.toISOString().slice(0, 10);
};

const formatBRL = (n: number) =>
  new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(n);

export const PartnerDashboardPage: React.FC = () => {
  const { user, logout } = useAuth();
  const [selectedMachineId, setSelectedMachineId] = useState<string | null>(null);
  const [startDate, setStartDate] = useState(thirtyDaysAgo());
  const [endDate, setEndDate] = useState(today());

  useEffect(() => {
    document.title = 'UpCar Parceiro';
    return () => {
      document.title = 'UpCar Aspiradores';
    };
  }, []);

  const machinesQuery = useQuery({
    queryKey: ['partner-machines'],
    queryFn: async () => {
      const res = await api.get('/partner/machines');
      return (res.data?.machines || []) as PartnerMachine[];
    },
  });

  // Default the report machine to the first one once loaded.
  useEffect(() => {
    if (!selectedMachineId && machinesQuery.data && machinesQuery.data.length > 0) {
      setSelectedMachineId(machinesQuery.data[0].id);
    }
  }, [machinesQuery.data, selectedMachineId]);

  const repasseQuery = useQuery({
    queryKey: ['partner-repasse', selectedMachineId, startDate, endDate],
    enabled: !!selectedMachineId,
    queryFn: async () => {
      const res = await api.get('/partner/repasse', {
        params: { machineId: selectedMachineId, startDate, endDate },
      });
      return res.data as RepasseReport;
    },
  });

  const partnerPercent = useMemo(
    () =>
      repasseQuery.data ? Math.round(repasseQuery.data.quotas.commercialPoint * 100) : null,
    [repasseQuery.data]
  );

  const FORMATS = {
    pdf: { path: 'pdf', ext: 'pdf', mime: 'application/pdf', label: 'PDF' },
    excel: {
      path: 'excel',
      ext: 'xlsx',
      mime: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      label: 'Excel',
    },
  } as const;

  const download = async (format: keyof typeof FORMATS) => {
    if (!selectedMachineId) return;
    const { path, ext, mime, label } = FORMATS[format];
    try {
      const res = await api.get(`/partner/repasse/${path}`, {
        params: { machineId: selectedMachineId, startDate, endDate },
        responseType: 'blob',
      });
      const blob = new Blob([res.data], { type: mime });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Repasse-UpCar-${repasseQuery.data?.machine.number || selectedMachineId}.${ext}`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      alert(`Falha ao baixar ${label}`);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-gradient-to-r from-orange-600 to-orange-500 text-white shadow">
        <div className="max-w-6xl mx-auto px-4 py-4 flex justify-between items-center">
          <div>
            <h1 className="text-xl font-bold">Painel do Parceiro</h1>
            <p className="text-sm text-orange-100">{user?.name}</p>
          </div>
          <button
            onClick={logout}
            className="px-4 py-2 bg-white/10 hover:bg-white/20 rounded-lg text-sm font-medium"
          >
            Sair
          </button>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-6 space-y-6">
        {/* Machines */}
        <section className="bg-white rounded-xl shadow p-6">
          <h2 className="text-lg font-semibold mb-4">Minhas máquinas</h2>
          {machinesQuery.isLoading ? (
            <p className="text-gray-500">Carregando…</p>
          ) : (machinesQuery.data || []).length === 0 ? (
            <p className="text-gray-500">Nenhuma máquina atribuída ainda.</p>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {machinesQuery.data!.map((m) => (
                <div
                  key={m.id}
                  className={`p-4 rounded-lg border-2 ${
                    selectedMachineId === m.id ? 'border-orange-500 bg-orange-50' : 'border-gray-200'
                  }`}
                >
                  <div className="font-semibold">{m.code}</div>
                  <div className="text-sm text-gray-600">{m.location}</div>
                  {m.city && <div className="text-xs text-gray-400">{m.city}</div>}
                  <button
                    onClick={() => setSelectedMachineId(m.id)}
                    className="mt-3 text-sm text-orange-600 hover:text-orange-700 font-medium"
                  >
                    Ver repasse →
                  </button>
                </div>
              ))}
            </div>
          )}
        </section>

        {/* Repasse */}
        <section className="bg-white rounded-xl shadow p-6">
          <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center mb-4 gap-3">
            <h2 className="text-lg font-semibold">Relatório de repasse</h2>
            <div className="flex flex-wrap gap-2 items-end">
              <div>
                <label className="block text-xs text-gray-500">Máquina</label>
                <select
                  value={selectedMachineId || ''}
                  onChange={(e) => setSelectedMachineId(e.target.value)}
                  className="px-3 py-2 border border-gray-300 rounded-lg"
                >
                  {(machinesQuery.data || []).map((m) => (
                    <option key={m.id} value={m.id}>
                      {m.code} — {m.location}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs text-gray-500">De</label>
                <input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  className="px-3 py-2 border border-gray-300 rounded-lg"
                />
              </div>
              <div>
                <label className="block text-xs text-gray-500">Até</label>
                <input
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  className="px-3 py-2 border border-gray-300 rounded-lg"
                />
              </div>
              <button
                onClick={() => download('pdf')}
                disabled={!repasseQuery.data}
                className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 disabled:opacity-50"
              >
                Baixar PDF
              </button>
              <button
                onClick={() => download('excel')}
                disabled={!repasseQuery.data}
                className="px-4 py-2 bg-green-700 text-white rounded-lg hover:bg-green-800 disabled:opacity-50"
              >
                Baixar Excel
              </button>
            </div>
          </div>

          {!selectedMachineId ? (
            <p className="text-gray-500">Selecione uma máquina para ver o repasse.</p>
          ) : repasseQuery.isLoading ? (
            <p className="text-gray-500">Calculando…</p>
          ) : repasseQuery.error ? (
            <p className="text-red-600">Erro ao carregar relatório.</p>
          ) : !repasseQuery.data ? null : (
            <div className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Stat
                  label={`Seu repasse (${partnerPercent}%)`}
                  value={formatBRL(repasseQuery.data.repasse.commercialPointReceivePay)}
                  highlight
                />
                <Stat label="Sessões" value={String(repasseQuery.data.usageHistory.length)} />
              </div>

              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200 text-sm">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-2 text-left font-medium text-gray-500">Data</th>
                      <th className="px-4 py-2 text-right font-medium text-gray-500">Duração</th>
                      <th className="px-4 py-2 text-right font-medium text-gray-500">Valor pago</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 bg-white">
                    {repasseQuery.data.usageHistory.length === 0 ? (
                      <tr>
                        <td colSpan={3} className="px-4 py-6 text-center text-gray-500">
                          Nenhuma sessão no período.
                        </td>
                      </tr>
                    ) : (
                      repasseQuery.data.usageHistory.map((h, idx) => (
                        <tr key={idx}>
                          <td className="px-4 py-2">{new Date(h.date).toLocaleDateString('pt-BR')}</td>
                          <td className="px-4 py-2 text-right">{h.duration} min</td>
                          <td className="px-4 py-2 text-right">{formatBRL(h.amount)}</td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </section>
      </main>
    </div>
  );
};

const Stat: React.FC<{ label: string; value: string; highlight?: boolean }> = ({ label, value, highlight }) => (
  <div className={`p-4 rounded-lg ${highlight ? 'bg-orange-50 border border-orange-200' : 'bg-gray-50'}`}>
    <div className="text-xs text-gray-500">{label}</div>
    <div className={`text-2xl font-bold ${highlight ? 'text-orange-700' : 'text-gray-900'}`}>{value}</div>
  </div>
);
